import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getUserData, saveGameState } from '@/lib/firebase/firestore';
import { Element as ElementType, Goal, GameState, GameStats } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/hooks/use-sonner-toast';
import { getRandomWord } from '@/lib/api';
import { initialElements } from '@/lib/constants';

// Define the context type
interface GameStateContextType {
  elements: ElementType[];
  favorites: ElementType[];
  discoveries: ElementType[];
  goals: Goal[];
  points: number;
  skipCount: number;
  keyCount: number;
  zoomLevel: number;
  wordHistory: {
    Item: string[];
    Location: string[];
    Character: string[];
  };
  combinations: Record<string, string>; // Format: "element1+element2" -> "result"
  deconstructions: Record<string, boolean>; // Format: "elementName" -> true
  stats: {
    totalDiscoveries: number;
    totalForged: number;
    itemsForged: number;
    locationsForged: number;
    charactersForged: number;
    goalsCompleted: number;
    skipsEarned: number;
    skipsUsed: number;
    elementsDeleted: number;
    keysCollected: number;
    keysUsed: number;
    deconstructions: number;
  };
  isLoading: boolean;
  setElements: (elements: ElementType[]) => void;
  setFavorites: (favorites: ElementType[]) => void;
  setDiscoveries: (discoveries: ElementType[]) => void;
  setGoals: (goals: Goal[]) => void;
  setPoints: (points: number) => void;
  setSkipCount: (skipCount: number) => void;
  setKeyCount: (keyCount: number) => void;
  setZoomLevel: (zoomLevel: number) => void;
  addToWordHistory: (type: 'Item' | 'Location' | 'Character', word: string) => void;
  addCombination: (element1: string, element2: string, result: string, forgeType: string) => void;
  hasCombination: (element1: string, element2: string, forgeType: string) => boolean;
  addDeconstruction: (element: string) => void;
  hasDeconstruction: (element: string) => boolean;
  resetGameState: () => Promise<void>;
  resetElements: () => void;
  deleteElement: (elementId: string) => void;
  incrementStat: (stat: keyof GameStats, amount?: number) => void;
}

// Create the context with default values
const GameStateContext = createContext<GameStateContextType>({
  elements: [],
  favorites: [],
  discoveries: [],
  goals: [],
  points: 0,
  skipCount: 0,
  keyCount: 0,
  zoomLevel: 2, // Default zoom level
  wordHistory: {
    Item: [],
    Location: [],
    Character: [],
  },
  combinations: {},
  deconstructions: {},
  stats: {
    totalDiscoveries: 0,
    totalForged: 0,
    itemsForged: 0,
    locationsForged: 0,
    charactersForged: 0,
    goalsCompleted: 0,
    skipsEarned: 0,
    skipsUsed: 0,
    elementsDeleted: 0,
    keysCollected: 0,
    keysUsed: 0,
    deconstructions: 0,
  },
  isLoading: true,
  setElements: () => {},
  setFavorites: () => {},
  setDiscoveries: () => {},
  setGoals: () => {},
  setPoints: () => {},
  setSkipCount: () => {},
  setKeyCount: () => {},
  setZoomLevel: () => {},
  addToWordHistory: () => {},
  addCombination: () => {},
  hasCombination: () => false,
  addDeconstruction: () => {},
  hasDeconstruction: () => false,
  resetGameState: async () => {},
  resetElements: () => {},
  deleteElement: () => {},
  incrementStat: () => {},
});

// Custom hook to use the context
export const useGameState = () => useContext(GameStateContext);

// Provider component
export const GameStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [elements, setElements] = useState<ElementType[]>(initialElements);
  const [favorites, setFavorites] = useState<ElementType[]>([]);
  const [discoveries, setDiscoveries] = useState<ElementType[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [points, setPoints] = useState<number>(0);
  const [skipCount, setSkipCount] = useState<number>(0);
  const [keyCount, setKeyCount] = useState<number>(0);
  const [zoomLevel, setZoomLevel] = useState<number>(2); // Default zoom level is 2
  const [wordHistory, setWordHistory] = useState<{
    Item: string[];
    Location: string[];
    Character: string[];
  }>({
    Item: [],
    Location: [],
    Character: [],
  });
  const [combinations, setCombinations] = useState<Record<string, string>>({});
  const [deconstructions, setDeconstructions] = useState<Record<string, boolean>>({});
  const [stats, setStats] = useState<GameStats>({
    totalDiscoveries: 0,
    totalForged: 0,
    itemsForged: 0,
    locationsForged: 0,
    charactersForged: 0,
    goalsCompleted: 0,
    skipsEarned: 0,
    skipsUsed: 0,
    elementsDeleted: 0,
    keysCollected: 0,
    keysUsed: 0,
    deconstructions: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastSavedState, setLastSavedState] = useState<string>('');

  // Remove getDefaultGoals as it's now handled by Firebase Functions

  // Add a word to the word history, keeping only the last 100 words
  const addToWordHistory = useCallback((type: 'Item' | 'Location' | 'Character', word: string) => {
    setWordHistory((prev) => {
      // Create a new array with the word at the beginning
      const newHistory = [word, ...prev[type].filter((w) => w !== word)];

      // Keep only the last 100 words
      const limitedHistory = newHistory.slice(0, 100);

      return {
        ...prev,
        [type]: limitedHistory,
      };
    });
  }, []);

  // Increment a stat by a specified amount (default: 1)
  const incrementStat = useCallback((stat: keyof GameStats, amount: number = 1) => {
    setStats((prev) => ({
      ...prev,
      [stat]: prev[stat] + amount,
    }));
  }, []);

  // Add a combination to the combinations record
  const addCombination = useCallback(
    (element1: string, element2: string, result: string, forgeType: string) => {
      // Create a consistent combination ID with forge type prefix (alphabetical order to ensure same ID regardless of order)
      const combinationId = `${forgeType}-${[element1, element2].sort().join('+')}`;

      setCombinations((prev) => ({
        ...prev,
        [combinationId]: result,
      }));
    },
    []
  );

  // Check if a combination has been tried before
  const hasCombination = useCallback(
    (element1: string, element2: string, forgeType: string) => {
      // Create a consistent combination ID with forge type prefix (alphabetical order to ensure same ID regardless of order)
      const combinationId = `${forgeType}-${[element1, element2].sort().join('+')}`;

      return combinations[combinationId] !== undefined;
    },
    [combinations]
  );

  // Add a deconstruction to the deconstructions record
  const addDeconstruction = useCallback((element: string) => {
    setDeconstructions((prev) => ({
      ...prev,
      [element]: true,
    }));
  }, []);

  // Check if a deconstruction has been tried before
  const hasDeconstruction = useCallback(
    (element: string) => {
      return deconstructions[element] === true;
    },
    [deconstructions]
  );

  // Reset game state
  const resetGameState = useCallback(async () => {
    setElements(initialElements);
    setFavorites([]);
    setDiscoveries([]);
    setGoals([]); // Let Firebase Functions handle initial goals
    setPoints(0);
    setSkipCount(0);
    setKeyCount(0);
    setZoomLevel(2); // Reset zoom level to default
    setWordHistory({
      Item: [],
      Location: [],
      Character: [],
    });
    setCombinations({});
    setDeconstructions({});
    setStats({
      totalDiscoveries: 0,
      totalForged: 0,
      itemsForged: 0,
      locationsForged: 0,
      charactersForged: 0,
      goalsCompleted: 0,
      skipsEarned: 0,
      skipsUsed: 0,
      elementsDeleted: 0,
      keysCollected: 0,
      keysUsed: 0,
      deconstructions: 0,
    });
  }, []);

  // Reset elements to just the starting 4
  const resetElements = useCallback(() => {
    // Keep only the starting elements
    setElements(initialElements);

    // Remove all discoveries
    setDiscoveries([]);

    // Remove any favorites that aren't in the initial elements
    const initialElementIds = initialElements.map((el) => el.id);
    setFavorites((prev) => prev.filter((fav) => initialElementIds.includes(fav.id)));

    // Save to database if user is logged in
    if (currentUser) {
      saveGameState(
        currentUser.uid,
        initialElements,
        favorites.filter((fav) => initialElementIds.includes(fav.id)),
        [],
        goals,
        points,
        skipCount,
        keyCount,
        zoomLevel,
        wordHistory,
        stats,
        {}, // Reset combinations
        {} // Reset deconstructions
      ).catch(() => {});
    }

    // Reset combinations and deconstructions
    setCombinations({});
    setDeconstructions({});
  }, [currentUser, favorites, goals, points, skipCount, keyCount, zoomLevel, wordHistory, stats]);

  // Delete a specific element
  const deleteElement = useCallback(
    (elementId: string) => {
      // Check if it's one of the initial elements (which can't be deleted)
      const isInitialElement = initialElements.some((el) => el.id === elementId);
      if (isInitialElement) {
        toast({
          title: 'Cannot Delete',
          description: 'The starting elements cannot be deleted.',
          variant: 'destructive',
        });
        return;
      }

      // Remove from elements
      setElements((prev) => prev.filter((el) => el.id !== elementId));

      // Remove from discoveries
      setDiscoveries((prev) => prev.filter((el) => el.id !== elementId));

      // Remove from favorites
      setFavorites((prev) => prev.filter((el) => el.id !== elementId));

      // Increment the elementsDeleted stat
      incrementStat('elementsDeleted');

      // Save to database if user is logged in
      if (currentUser) {
        const updatedElements = elements.filter((el) => el.id !== elementId);
        const updatedDiscoveries = discoveries.filter((el) => el.id !== elementId);
        const updatedFavorites = favorites.filter((el) => el.id !== elementId);

        saveGameState(
          currentUser.uid,
          updatedElements,
          updatedFavorites,
          updatedDiscoveries,
          goals,
          points,
          skipCount,
          keyCount,
          zoomLevel,
          wordHistory,
          stats,
          combinations,
          deconstructions
        ).catch(() => {});
      }
    },
    [
      currentUser,
      elements,
      discoveries,
      favorites,
      goals,
      points,
      skipCount,
      keyCount,
      zoomLevel,
      wordHistory,
      stats,
      combinations,
      deconstructions,
      toast,
      incrementStat,
    ]
  );

  // Load user data when the user changes
  useEffect(() => {
    const loadUserData = async () => {
      setIsLoading(true);

      if (currentUser) {
        try {
          const userData = await getUserData(currentUser.uid);
          if (userData && userData.gameState) {
            setElements(userData.gameState.elements || initialElements);
            setFavorites(userData.gameState.favorites || []);
            setDiscoveries(userData.gameState.discoveries || []);
            setGoals(userData.gameState.goals || []);
            setPoints(userData.gameState.points || 0);
            setSkipCount(userData.gameState.skipCount || 0);
            setKeyCount(userData.gameState.keyCount || 0);
            setZoomLevel(userData.gameState.zoomLevel || 2); // Load zoom level or use default
            setWordHistory(
              userData.gameState.wordHistory || {
                Item: [],
                Location: [],
                Character: [],
              }
            );
            setCombinations(userData.gameState.combinations || {});
            setDeconstructions(userData.gameState.deconstructions || {});
            setStats(
              userData.gameState.stats || {
                totalDiscoveries: 0,
                totalForged: 0,
                itemsForged: 0,
                locationsForged: 0,
                charactersForged: 0,
                goalsCompleted: 0,
                skipsEarned: 0,
                skipsUsed: 0,
                elementsDeleted: 0,
                keysCollected: 0,
                keysUsed: 0,
                deconstructions: 0,
              }
            );
          }
          // Remove else block - new user initialization is handled by Firebase Functions
        } catch (error) {
          throw error;
        }
      } else {
        // For anonymous users, just reset to empty state
        await resetGameState();
      }

      setIsLoading(false);
    };

    loadUserData();
  }, [currentUser, resetGameState]);

  // Auto-save game state for logged-in users
  useEffect(() => {
    if (!currentUser || isLoading) return;

    // Create a string representation of the current state for comparison
    const currentState = JSON.stringify({
      elements,
      favorites,
      discoveries,
      goals,
      points,
      skipCount,
      keyCount,
      zoomLevel,
      wordHistory,
      combinations,
      deconstructions,
      stats,
    });

    // Check if state has changed
    if (currentState !== lastSavedState) {
      const saveTimeout = setTimeout(async () => {
        try {
          const success = await saveGameState(
            currentUser.uid,
            elements,
            favorites,
            discoveries,
            goals,
            points,
            skipCount,
            keyCount,
            zoomLevel,
            wordHistory,
            stats,
            combinations,
            deconstructions
          );

          if (success) {
            setLastSavedState(currentState);
          } else {
          }
        } catch (error) {}
      }, 2000); // Debounce saves to avoid too many writes

      return () => clearTimeout(saveTimeout);
    }
  }, [
    currentUser,
    elements,
    favorites,
    discoveries,
    goals,
    points,
    skipCount,
    keyCount,
    zoomLevel,
    wordHistory,
    combinations,
    deconstructions,
    stats,
    lastSavedState,
    isLoading,
  ]);

  const value: GameStateContextType = {
    elements,
    favorites,
    discoveries,
    goals,
    points,
    skipCount,
    keyCount,
    zoomLevel,
    wordHistory,
    combinations,
    deconstructions,
    stats,
    isLoading,
    setElements,
    setFavorites,
    setDiscoveries,
    setGoals,
    setPoints,
    setSkipCount,
    setKeyCount,
    setZoomLevel,
    addToWordHistory,
    addCombination,
    hasCombination,
    addDeconstruction,
    hasDeconstruction,
    resetGameState,
    resetElements,
    deleteElement,
    incrementStat,
  };

  return <GameStateContext.Provider value={value}>{children}</GameStateContext.Provider>;
};
